import React, { useMemo } from 'react';
import { Box, Slider, Typography, styled } from '@mui/material';

import { useStore } from 'hooks';
import { useTranslation } from 'react-i18next';

const AlertSlider = ({ className, value, defaultValue = 50, min = -20, max = 100 }) => {
  const { t } = useTranslation();
  const { establishmentStore } = useStore();
  const DELTA = establishmentStore.unitPreference === 'English' ? 20 : 15;
  const temperatureUnit = establishmentStore.temperatureUnit;

  const limit = useMemo(() => {
    const minLimit = min - DELTA;
    const maxLimit = max + DELTA;
    return { min: minLimit, max: maxLimit };
  }, [min, max, DELTA]);

  const colorZones = useMemo(() => {
    const rangeStart = limit.min;
    const rangeEnd = limit.max;
    const lowColorZone = ((min - rangeStart) / (rangeEnd - rangeStart)) * 100;
    const highColorZone = ((max - rangeStart) / (rangeEnd - rangeStart)) * 100;
    return { min: Math.round(lowColorZone), max: Math.round(highColorZone) };
  }, [min, max, limit]);

  return (
    <StyledRootBox className={className}>
      <Slider
        disabled
        min={limit.min}
        max={limit.max}
        track={false}
        defaultValue={defaultValue}
        value={Number.isNaN(+value) ? defaultValue : value}
        valueLabelFormat={Number.isNaN(+value) ? 'N/A' : `${value?.toFixed(1)} ${temperatureUnit}`}
        className="slider"
        valueLabelDisplay="on"
        sx={{
          '& .MuiSlider-rail': {
            opacity: 1,
            background: `linear-gradient(to right, #2196F3 0% ${colorZones.min - 1}%, transparent ${
              colorZones.min - 1
            }% ${colorZones.min}%, #66BB6A ${colorZones.min}% ${colorZones.max - 1}%, transparent ${
              colorZones.max - 1
            }% ${colorZones.max}%, #FF7A7A ${colorZones.max}% 100%)`,
          },
        }}
      />
      <Box className="alert_slider_description_box">
        <Typography variant="body2">{t('shared.AlertSlider.low')}</Typography>
        <Typography variant="body2">
          {t('shared.AlertSlider.normal')} ({min?.toFixed(1) || 'N/A'}
          {temperatureUnit} - {max?.toFixed(1) || 'N/A'}
          {temperatureUnit})
        </Typography>
        <Typography variant="body2">{t('shared.AlertSlider.high')}</Typography>
      </Box>
    </StyledRootBox>
  );
};

export default AlertSlider;

const StyledRootBox = styled(Box)(({ theme: { breakpoints } }) => ({
  width: '100%',
  [breakpoints.down('sm')]: {
    padding: '0 8px',
  },

  '.alert_slider_description_box': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',

    '&.center': {
      justifyContent: 'center',
    },
  },

  '.slider': {
    padding: '6px 0px',

    '& .MuiSlider-thumb': {
      width: '2px',
      color: '#FFFFFF',
      borderRadius: 0,

      boxShadow: '0 0 2px 0px rgba(0, 0, 0, 0.1)',
      '&:focus, &:hover, &.Mui-active': {
        boxShadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.1)',
      },
      '&:before': {
        boxShadow:
          '0px 0px 1px 0px rgba(0,0,0,0.2), 0px 0px 0px 0px rgba(0,0,0,0.14), 0px 0px 1px 0px rgba(0,0,0,0.12)',
      },
    },

    '& .MuiSlider-valueLabel': {
      fontSize: 12,
      fontWeight: 'normal',
      top: -6,
      backgroundColor: 'unset',
      padding: '0px 5px',
      border: '1px solid #FF7A7A',
      '&::before': {
        display: 'none',
      },
      '& *': {
        background: 'transparent',
        // color: theme.palette.mode === 'dark' ? '#fff' : '#000',
      },
    },
  },
}));
